import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { DropdownItem, RowSelectionState, M, Button } from 'design-system-web';
import { bulkAssignMemberMutation } from 'graphql-queries';
import { Dropdown } from 'components';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { Assignable } from 'domains-work-order-type-contracts';

/** @deprecated Use Assignable instead */
type AdminUser = Assignable;

const SERVICE_REQUEST_SELECTION_LIMIT = 100;

type Props = {
  rowSelection?: RowSelectionState;
  assignableUsers: AdminUser[];
  refetch?: () => void;
};

export function BulkAssigneeAction({
  rowSelection,
  assignableUsers,
  refetch,
}: Props) {
  const { t } = useTranslation();
  const [bulkAssignMember] = useMutation(bulkAssignMemberMutation);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(false);

  const selectedWorkOrderIds = useMemo(() => {
    if (!rowSelection) {
      return [];
    }

    return Object.entries(rowSelection)
      .filter(([, isSelected]) => isSelected)
      .map(([workOrderId]) => workOrderId);
  }, [rowSelection]);

  const openModal = async () => {
    if (selectedWorkOrderIds.length === 0) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignMember.noSelection')
      );

      return;
    }

    if (selectedWorkOrderIds.length > SERVICE_REQUEST_SELECTION_LIMIT) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignMember.limitExceeded', {
          serviceRequestSelectionLimit: SERVICE_REQUEST_SELECTION_LIMIT,
          selectedWorkOrderCount: selectedWorkOrderIds.length,
        }),
        5000
      );

      return;
    }

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleConfirm = async () => {
    if (!selectedUser) {
      return;
    }

    if (selectedWorkOrderIds.length > SERVICE_REQUEST_SELECTION_LIMIT) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignMember.limitExceeded', {
          serviceRequestSelectionLimit: SERVICE_REQUEST_SELECTION_LIMIT,
          selectedWorkOrderCount: selectedWorkOrderIds.length,
        }),
        5000
      );

      closeModal();

      return;
    }

    setLoading(true);

    try {
      const result = await bulkAssignMember({
        variables: {
          serviceRequestIds: selectedWorkOrderIds,
          assignee: selectedUser.value,
        },
      });

      const { successCount, failureCount } = result.data?.bulkAssignMember || {
        successCount: 0,
        failureCount: selectedWorkOrderIds.length,
      };

      if (failureCount === 0) {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignMember.success', {
            count: successCount,
          })
        );
      } else if (successCount === 0) {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignMember.error')
        );
      } else {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignMember.partial', {
            successCount,
            failureCount,
          }),
          5000
        );
      }

      if (refetch) {
        refetch();
      }
    } catch {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignMember.error')
      );
    }

    setLoading(false);
    closeModal();
  };

  return (
    <>
      <Button
        variant="secondary"
        size="large"
        onClick={openModal}
        testId="bulk-assignee-select"
      >
        {t('web.admin.serviceRequest.bulkActions.assignMember.selectMember')}
      </Button>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        handleConfirm={handleConfirm}
        title={t(
          'web.admin.serviceRequest.bulkActions.assignMember.confirm.title'
        )}
        confirmText={t(
          'web.admin.serviceRequest.bulkActions.assignMember.confirm.button'
        )}
        cancelText={t(
          'web.admin.serviceRequest.bulkActions.assignMember.cancel.button'
        )}
        loading={loading}
        confirmActionDisabled={!selectedUser || loading}
        cancelActionDisabled={loading}
        isFullWidth
      >
        <M>
          {t(
            'web.admin.serviceRequest.bulkActions.assignMember.confirm.message',
            {
              count: selectedWorkOrderIds.length,
            }
          )}
        </M>

        <Dropdown
          isSearchable
          value={selectedUser?.value || ''}
          items={assignableUsers || []}
          onChange={(item: DropdownItem<string>) => {
            const matchedUser = assignableUsers.find(
              user => user.value === item.value
            );

            if (matchedUser) {
              setSelectedUser(matchedUser);
            }
          }}
          placeholder={t(
            'web.admin.serviceRequest.bulkActions.assignMember.selectMember'
          )}
          doTranslation={false}
        />
      </ConfirmationModal>
    </>
  );
}
